import styled, { css } from 'styled-components';
import { Button } from '@/components/UI/Button/Button';

const controlBase = css`
  white-space: nowrap;
  gap: 12px;
  height: 44px;
  min-height: 44px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.palette.secondary.border};
  background: ${({ theme }) => theme.palette.primary.background};
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 14px;
  font-weight: 500;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.palette.brand.main};

    & path {
      fill: ${({ theme }) => theme.palette.brand.main};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: auto;
    min-height: auto;
  }
`;

export const Control = styled(Button)<{
  $sortOpen?: boolean;
  $categoryOpen?: boolean;
  $categoryHasValue?: boolean;
}>`
  ${controlBase}

  ${({ $sortOpen }) =>
    typeof $sortOpen === 'boolean' &&
    css`
      position: relative;
      background-repeat: no-repeat;
      background-position: left 16px center;
      background-size: 20px;
      background-image: url(${$sortOpen
        ? '/images/shops/sort.svg'
        : '/images/shops/sort-closed.svg'});
      padding-left: 44px;
      padding-right: 44px;

      &::after {
        content: '';
        position: absolute;
        right: 16px;
        top: 50%;
        width: 15px;
        height: 15px;
        transform: translateY(-50%);
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-image: url(${$sortOpen
          ? '/images/shops/sort-icon.svg'
          : '/images/shops/sort-icon-closed.svg'});
      }
    `}

  ${({ $categoryOpen }) =>
    typeof $categoryOpen === 'boolean' &&
    css`
      position: relative;
      padding-right: 44px;

      &::after {
        content: '';
        position: absolute;
        right: 16px;
        top: 50%;
        width: 15px;
        height: 15px;
        transform: translateY(-50%);
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        background-image: url(${$categoryOpen
          ? '/images/shops/sort-icon.svg'
          : '/images/shops/sort-icon-closed.svg'});
      }
    `}

  ${({ $categoryHasValue }) =>
    $categoryHasValue &&
    css`
      &::after {
        display: none;
      }
      padding-right: 44px;

      & > svg:last-child {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;
      }
    `}
`;

export const CategoryControlWrap = styled.div<{ $isActive?: boolean }>`
  position: relative;
  z-index: 2;

  & ${Control} {
    min-width: 120px;
    background: ${({ theme }) => theme.palette.primary.background};
    justify-content: flex-start;
    text-align: left;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    & ${Control} {
      height: 35px;
      min-height: 35px;
      border-radius: 100px;
      background: ${({ theme }) => theme.palette.secondary.background};
      border: none;
      min-width: 0;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    & ${Control} {
      width: 300px;
      min-width: 300px;
    }

    ${({ $isActive, theme }) =>
      $isActive &&
      css`
        & ${Control} {
          border: 1px solid ${theme.palette.brand.main};
          background: ${theme.palette.quartenary.background};
        }
      `}
  }
`;

export const CategoryImage = styled.img`
  object-fit: contain;
  max-width: 27px;
  max-height: 24px;
`;

export const CategoryDropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  min-width: 260px;
  max-height: 360px;
  padding: 8px 0;
  border-radius: 12px;
  background: ${({ theme }) => theme.palette.system.white};
  border: 1px solid ${({ theme }) => theme.palette.secondary.border};
  box-shadow: 0 4px 16px ${({ theme }) => theme.palette.overlay[8]};
  overflow-y: auto;
`;

export const CategoryMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const CategoryMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s ease, color 0.1s ease;

  &:hover {
    color: ${({ theme }) => theme.palette.brand.main};
  }
`;

export const CategoryItemIconWrap = styled.span`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CategoryItemIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
`;

export const CategoryItemIconFallback = styled.span`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.secondary.labels};

  & svg {
    width: 20px;
    height: 20px;
  }
`;

export const CategoryItemName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CategoryMenuItemSkeleton = styled.div`
  height: 44px;
  margin: 2px 14px;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.secondary.background};
  animation: categorySkeletonPulse 1.2s ease-in-out infinite;

  @keyframes categorySkeletonPulse {
    0%,
    100% {
      opacity: 0.6;
    }

    50% {
      opacity: 1;
    }
  }
`;
